import { useState, FC, ReactNode } from 'react';
import { ToTop } from '../modules';

type SectionProps = {
    title: string;
    children: ReactNode;
};

const Section: FC<SectionProps> = ({ title, children }) => (
    <section style={{ marginBottom: '2.5rem' }}>
        <h2
            className="privacypolicy_contents_h2"
            style={{
                borderBottom: '2px solid #eee',
                paddingBottom: '0.5rem',
                marginBottom: '1.5rem',
            }}
        >
            {title}
        </h2>
        <div style={{ lineHeight: 1.8, color: '#555' }}>{children}</div>
    </section>
);

type PolicyHeaderProps = {
    title: string;
    subTitle: string;
    lastUpdate: string;
};

const PolicyHeader: FC<PolicyHeaderProps> = ({
    title,
    subTitle,
    lastUpdate,
}) => (
    <header style={{ textAlign: 'center', marginBottom: '4rem' }}>
        <h1 className="privacypolicy_title" style={{ marginBottom: '0.5rem' }}>
            {title}
        </h1>
        <h2
            className="privacypolicy_subTitle"
            style={{ marginBottom: '1rem', color: '#666' }}
        >
            {subTitle}
        </h2>
        <p
            className="privacypolicy_contents_body"
            style={{ color: '#888', fontSize: '0.9rem' }}
        >
            {lastUpdate}
        </p>
    </header>
);

type LangButtonProps = {
    onClick: () => void;
    isActive: boolean;
    children: ReactNode;
    style?: React.CSSProperties;
};

const LangButton: FC<LangButtonProps> = ({
    onClick,
    isActive,
    children,
    style,
}) => (
    <button
        onClick={onClick}
        style={{
            ...style,
            padding: '8px 16px',
            cursor: 'pointer',
            border: isActive ? '1px solid #333' : '1px solid #ccc',
            backgroundColor: isActive ? '#333' : 'white',
            color: isActive ? 'white' : '#333',
            borderRadius: '4px',
            transition: 'all 0.3s',
            minWidth: '110px',
            whiteSpace: 'nowrap',
            height: '40px',
        }}
    >
        {children}
    </button>
);

const FlowIntervalPrivacypolicy: FC = () => {
    const [lang, setLang] = useState<'jp' | 'en'>('jp');

    const enContent = {
        title: 'Flow Interval',
        subTitle: 'Privacy Policy',
        lastUpdate: 'Effective Date: 2025-12-27',
        introduction:
            'This privacy policy applies to the Flow Interval app (hereby referred to as "Application") for mobile devices that was created by Takumi Kanaya (hereby referred to as "Service Provider") as a Freemium service. This service is intended for use "AS IS".',
        section1Title: 'Information Collection and Use',
        section1Body: (
            <>
                <p className="privacypolicy_contents_body" style={{ marginBottom: '1rem' }}>
                    The Application <strong>does not</strong> collect, store, or transmit any Personally Identifiable Information (PII) such as your name, address, email address, or phone number.
                </p>
                <p className="privacypolicy_contents_body">
                    Furthermore, the Application does not track your location, IP address, or usage history using third-party analytics services.
                </p>
            </>
        ),
        section2Title: 'Data Storage',
        section2Body:
            'All data created within the Application (including timer presets, steps, and settings) is stored locally on your device. The Service Provider does not maintain any external servers to store your data.',
        section3Title: 'In-App Purchases',
        section3Body:
            "The Application offers In-App Purchases. All transactions are processed securely through Apple's App Store payment system. The Service Provider does not have access to your credit card information or billing details.",
        section4Title: 'Contacting You',
        section4Body:
            'Since the Service Provider does not collect your email address or contact information, we will not send you any marketing promotions or notices. We will only contact you if you reach out to us first for support.',
        section5Title: 'Opt-Out Rights and Data Deletion',
        section5Body:
            "You can stop all use of the Application and delete all data generated by the Application easily by uninstalling it. Since no data is transmitted to the Service Provider's servers, uninstalling the Application ensures that all your data is removed from your device.",
        section6Title: 'Children',
        section6Body:
            'The Service Provider does not knowingly collect personally identifiable information from children under the age of 13.',
        section7Title: 'Security',
        section7Body:
            'The Service Provider is concerned about safeguarding the confidentiality of your information. However, since the Application does not transmit your personal data over the internet, the security of your data largely depends on the security of your own device.',
        section8Title: 'Changes',
        section8Body:
            'This Privacy Policy may be updated from time to time. The Service Provider will notify you of any changes by updating the Privacy Policy page.',
        section9Title: 'Effective Date',
        section9Body: 'This privacy policy is effective as of 2025-12-27.',
        section10Title: 'Your Consent',
        section10Body:
            'By using the Application, you are consenting to the terms set forth in this Privacy Policy.',
        section11Title: 'Contact Us',
        section11Body:
            'If you have any questions regarding privacy while using the Application, please contact the Service Provider via email at budoudesign.test@gmail.com.',
    };

    const jpContent = {
        title: 'Flow Interval',
        subTitle: 'プライバシーポリシー',
        lastUpdate: '発効日: 2025年12月27日',
        introduction:
            '本プライバシーポリシーは、Takumi Kanaya（以下「サービス提供者」）がFreemiumサービスとして作成したモバイル端末向けアプリ「Flow Interval」（以下「本アプリ」）に適用されます。本サービスは「現状有姿」で提供されます。',
        section1Title: '情報の収集と利用',
        section1Body: (
            <>
                <p className="privacypolicy_contents_body" style={{ marginBottom: '1rem' }}>
                    本アプリは、氏名、住所、メールアドレス、電話番号などの個人を特定できる情報（PII）を収集、保存、送信することはありません。
                </p>
                <p className="privacypolicy_contents_body">
                    さらに、本アプリは、サードパーティの分析サービスを使用して、位置情報、IPアドレス、または使用履歴を追跡することはありません。
                </p>
            </>
        ),
        section2Title: 'データの保存',
        section2Body:
            '本アプリ内で作成されたすべてのデータ（タイマーのプリセット、ステップ、設定を含む）は、お使いのデバイスにローカルに保存されます。サービス提供者は、お客様のデータを保存するための外部サーバーを保持していません。',
        section3Title: 'アプリ内課金',
        section3Body:
            '本アプリはアプリ内課金を提供しています。すべての取引は、AppleのApp Store決済システムを通じて安全に処理されます。サービス提供者は、お客様のクレジットカード情報や請求詳細にアクセスすることはありません。',
        section4Title: 'お客様への連絡',
        section4Body:
            'サービス提供者はお客様のメールアドレスや連絡先情報を収集しないため、マーケティングプロモーションや通知をお送りすることはありません。お客様からサポートのお問い合わせがあった場合にのみ、ご連絡いたします。',
        section5Title: 'オプトアウト権とデータの削除',
        section5Body:
            '本アプリをアンインストールすることで、本アプリのすべての使用を停止し、本アプリによって生成されたすべてのデータを簡単に削除することができます。サービス提供者のサーバーにデータが送信されることはないため、本アプリをアンインストールすることで、すべてのデータがお使いのデバイスから確実に削除されます。',
        section6Title: '子供のプライバシー',
        section6Body:
            'サービス提供者は、13歳未満の子供から意図的に個人を特定できる情報を収集することはありません。',
        section7Title: 'セキュリティ',
        section7Body:
            'サービス提供者は、お客様の情報の機密性を保護することに関心を持っています。ただし、本アプリはお客様の個人データをインターネット経由で送信しないため、データのセキュリティは主にお客様自身のデバイスのセキュリティに依存します。',
        section8Title: '変更',
        section8Body:
            '本プライバシーポリシーは、随時更新される可能性があります。サービス提供者は、プライバシーポリシーのページを更新することにより、変更を通知します。',
        section9Title: '発効日',
        section9Body: '本プライバシーポリシーは、2025年12月27日より有効です。',
        section10Title: 'お客様の同意',
        section10Body:
            '本アプリを使用することにより、お客様は本プライバシーポリシーに記載されている条件に同意したものとみなされます。',
        section11Title: 'お問い合わせ',
        section11Body:
            '本アプリの使用中のプライバシーに関するご質問がある場合は、メール（budoudesign.test@gmail.com）にてサービス提供者までお問い合わせください。',
    };

    const currentContent = lang === 'jp' ? jpContent : enContent;

    return (
        <div
            className="privacypolicy"
            style={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: '#fff',
                padding: '4rem 2rem',
            }}
        >
            <ToTop />
            <div
                className="privacypolicy_contents"
                style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    fontFamily: 'sans-serif',
                }}
            >
                <div style={{ textAlign: 'right', marginBottom: '2rem' }}>
                    <LangButton
                        onClick={() => setLang('jp')}
                        isActive={lang === 'jp'}
                        style={{ marginRight: '10px' }}
                    >
                        日本語
                    </LangButton>
                    <LangButton onClick={() => setLang('en')} isActive={lang === 'en'}>
                        English
                    </LangButton>
                </div>

                <PolicyHeader
                    title={currentContent.title}
                    subTitle={currentContent.subTitle}
                    lastUpdate={currentContent.lastUpdate}
                />

                <p
                    className="privacypolicy_contents_body"
                    style={{ marginBottom: '2.5rem' }}
                >
                    {currentContent.introduction}
                </p>

                <Section title={currentContent.section1Title}>
                    {currentContent.section1Body}
                </Section>

                <Section title={currentContent.section2Title}>
                    <p className="privacypolicy_contents_body">
                        {currentContent.section2Body}
                    </p>
                </Section>

                <Section title={currentContent.section3Title}>
                    <p className="privacypolicy_contents_body">
                        {currentContent.section3Body}
                    </p>
                </Section>

                <Section title={currentContent.section4Title}>
                    <p className="privacypolicy_contents_body">
                        {currentContent.section4Body}
                    </p>
                </Section>

                <Section title={currentContent.section5Title}>
                    <p className="privacypolicy_contents_body">
                        {currentContent.section5Body}
                    </p>
                </Section>

                <Section title={currentContent.section6Title}>
                    <p className="privacypolicy_contents_body">
                        {currentContent.section6Body}
                    </p>
                </Section>

                <Section title={currentContent.section7Title}>
                    <p className="privacypolicy_contents_body">
                        {currentContent.section7Body}
                    </p>
                </Section>

                <Section title={currentContent.section8Title}>
                    <p className="privacypolicy_contents_body">
                        {currentContent.section8Body}
                    </p>
                </Section>

                <Section title={currentContent.section9Title}>
                    <p className="privacypolicy_contents_body">
                        {currentContent.section9Body}
                    </p>
                </Section>

                <Section title={currentContent.section10Title}>
                    <p className="privacypolicy_contents_body">
                        {currentContent.section10Body}
                    </p>
                </Section>

                <Section title={currentContent.section11Title}>
                    <p className="privacypolicy_contents_body">
                        {currentContent.section11Body}
                    </p>
                </Section>
            </div>
        </div>
    );
};

export default FlowIntervalPrivacypolicy;
